// Единый каталог курсов: канон таксономии + схемы выдачи.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему две ортогональные оси, а не один `category`
// ─────────────────────────────────────────────────────────────────────────────
// Легаси-список категорий авторских шаблонов смешивал предмет и формат: `exam`
// лежал рядом с `science` и `language`, поэтому «подготовка к ЕГЭ по биологии»
// не могла быть одновременно экзаменом и биологией — фильтр вынуждал выбрать
// одно. Каталог разведён на две независимые оси:
//
//   subjectArea — ЧЕМУ учит курс (предметная область);
//   purpose     — ЗАЧЕМ он нужен (подготовка к экзамену либо общий курс).
//
// Третья ось — `origin` (кто автор) — НЕ колонка: она выводится из того, из
// какой таблицы пришла запись, и клиент её не задаёт.
//
// Каталог склеивает два источника (`study_plan_templates` — программы платформы,
// `course_templates` — курсы авторов), поэтому таксономия обязана быть общей:
// свои enum'ы у каждого источника означали бы, что универсальный фильтр
// невозможен в принципе.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему цена и витринные тексты живут ЗДЕСЬ, а не в `public.schemas.ts`
// ─────────────────────────────────────────────────────────────────────────────
// Ровно по той же причине, что и таксономия: это кросс-источниковый канон.
// Цену читают карточка каталога, витрина курса и админская мутация; витринные
// тексты (`listing_*`) миграция 0118 добавляет ОБОИМ источникам, и мастер
// публикации правит те же поля, что показывает публичная страница.
//
// Технически это ещё и единственное место, откуда обе формы видны всем
// потребителям без цикла импортов: `schemas.ts` и `public.schemas.ts` тянут
// этот файл, а он — никого из соседей. Определи цену в `public.schemas.ts` —
// и `catalog.schemas.ts → public.schemas.ts → catalog.schemas.ts` замкнётся,
// а circular ESM-импорт здесь падает не типом, а `undefined` внутри `z.object`
// в рантайме.
import { z } from "zod";
import {
	CATALOG_MAX_PAGE,
	CATALOG_PAGE_SIZE,
	catalogPageOffset,
} from "../../utils/catalog-pagination.js";
import { localeSchema } from "../_shared/common.js";
import { courseCoverSchema } from "../_shared/cover.js";
import { cursorPaginationInput, cursorPaginationOutput } from "../_shared/pagination.js";

// ===== Ось 1: предметная область =====

/**
 * Предметная область — ЧЕМУ учит курс. Только предмет: формата («экзамен»),
 * уровня и языка здесь быть не может, для них отдельные оси.
 *
 * Список сознательно короткий и плоский: иерархия «направление → тема» (как в
 * больших каталогах) потребует связующей таблицы и мульти-выбора, а пустые
 * рубрики хуже отсутствия рубрик.
 */
export const COURSE_SUBJECT_AREAS = [
	"math",
	"science",
	"languages",
	"humanities",
	"tech",
	"business",
	"medicine",
	"arts",
	"countries",
	"other",
] as const;
export const courseSubjectAreaSchema = z.enum(COURSE_SUBJECT_AREAS);
export type CourseSubjectArea = (typeof COURSE_SUBJECT_AREAS)[number];

// ===== Ось 2: назначение =====

/**
 * Назначение курса.
 *  - `exam`    — подготовка к конкретному экзамену (ЕГЭ, SAT, IELTS…): у такого
 *                курса осмысленны фильтры страны и образовательной подсистемы;
 *  - `general` — общий курс по теме, без привязки к экзамену.
 */
export const COURSE_PURPOSES = ["exam", "general"] as const;
export const coursePurposeSchema = z.enum(COURSE_PURPOSES);
export type CoursePurpose = (typeof COURSE_PURPOSES)[number];

// ===== Ось 3: источник (производная, не колонка) =====

/**
 * Кто стоит за курсом.
 *  - `official` — программа платформы (`study_plan_templates`): каркас под
 *                 экзамен, курс собирается генерацией под даты ученика;
 *  - `author`   — курс, опубликованный пользователем (`course_templates`):
 *                 готовый снимок со структурой, конспектами и артефактами.
 *
 * Значение выводит СЕРВЕР по таблице-источнику. Клиент его только фильтрует и
 * показывает бейджем — подменить происхождение запросом нельзя.
 */
export const CATALOG_ORIGINS = ["official", "author"] as const;
export const catalogOriginSchema = z.enum(CATALOG_ORIGINS);
export type CatalogOrigin = (typeof CATALOG_ORIGINS)[number];

// ===== Уровень =====

export const COURSE_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export const courseLevelSchema = z.enum(COURSE_LEVELS);
export type CourseLevel = (typeof COURSE_LEVELS)[number];

// ===== Ось 4: цена =====

/**
 * Модель оплаты курса.
 *
 * Два значения, а не «цена 0 = бесплатно»: «бесплатный» и «платный, но цена
 * ещё не выставлена» — разные состояния витрины (в первом кнопка «Начать
 * курс», во втором её показывать нельзя), а нулём их не различить.
 */
export const COURSE_PRICING_KINDS = ["free", "paid"] as const;
export const coursePricingKindSchema = z.enum(COURSE_PRICING_KINDS);
export type CoursePricingKind = (typeof COURSE_PRICING_KINDS)[number];

/**
 * Валюта цен курса — только USD, решение владельца продукта.
 *
 * Литерал, а не enum из одного элемента: `z.literal('USD')` делает невозможным
 * появление второй валюты «случайно», через сохранение чужого значения. Придёт
 * мультивалютность — она потребует не расширения enum'а, а курсов конвертации,
 * витрины «цена в вашей валюте» и колонки в покупках; сейчас честнее прибить
 * гвоздём, чем изображать поддержку.
 */
export const COURSE_PRICING_CURRENCY = "USD" as const;

/**
 * Цена курса. `priceMinor` — МИНОРНЫЕ единицы (центы), как везде в проекте:
 * дробные доллары во float дают классическую ошибку округления на сумме.
 *
 * Инвариант пары полей (его же держит CHECK миграции 0119):
 *   `kind: 'free'` → `priceMinor: null`;
 *   `kind: 'paid'` → `priceMinor > 0`.
 * В схеме ВЫДАЧИ он не проверяется через `.refine` намеренно: ответ сервера уже
 * прошёл constraint базы, а лишний refine на каждой карточке каталога — это
 * работа парсера на ровном месте. Проверка живёт там, где значение приходит
 * снаружи, — во входе админской мутации (`setCoursePricingInput`).
 */
export const coursePricingSchema = z.object({
	kind: coursePricingKindSchema,
	priceMinor: z.number().int().positive().nullable(),
	currency: z.literal(COURSE_PRICING_CURRENCY),
});
export type CoursePricing = z.infer<typeof coursePricingSchema>;

/** Бесплатный курс — единственная форма «цены нет» на всех поверхностях. */
export const FREE_PRICING: CoursePricing = {
	kind: "free",
	priceMinor: null,
	currency: COURSE_PRICING_CURRENCY,
};

// ===== Подпись автора =====

/**
 * Как курс подписан на витрине — ВЫБОР АВТОРА.
 *  - `user`      — карточка автора: имя, аватар, био, ссылка;
 *  - `anonymous` — курс выходит без указания автора.
 *
 * Варианта «от имени платформы» здесь НЕТ и быть не может. Подпись StudyHub —
 * это заявление платформы об авторстве и поручительство за содержание; она
 * выводится из ПРОИСХОЖДЕНИЯ строки (`origin='official'`, программы платформы),
 * а не из галочки в мастере. Пока эти две вещи жили в одном перечислении,
 * пользователь мог присвоить своему курсу наш знак качества, и публичная
 * страница честно писала «подготовлен командой StudyHub».
 *
 * Значение живёт в нижнем слое, потому что его читают обе стороны: вход
 * публикации (`courseTemplatesPublishInput`) и публичный ответ
 * (`courseAuthorPublicSchema`). По умолчанию — `user`.
 */
export const COURSE_AUTHOR_DISPLAYS = ["user", "anonymous"] as const;
export const courseAuthorDisplaySchema = z.enum(COURSE_AUTHOR_DISPLAYS);
export type CourseAuthorDisplay = (typeof COURSE_AUTHOR_DISPLAYS)[number];

/**
 * Как курс подписан на витрине — ЧТО МОЖЕТ ПОКАЗАТЬ СТРАНИЦА.
 *
 * Шире авторского выбора ровно на `platform`: официальная половина каталога
 * (`study_plan_templates`) подписана платформой, и публичный ответ обязан уметь
 * это выразить. Разные перечисления — не дублирование, а разные вопросы:
 * «что автору разрешено выбрать» и «что читатель может увидеть».
 */
export const COURSE_PUBLIC_AUTHOR_DISPLAYS = ["user", "anonymous", "platform"] as const;
export const coursePublicAuthorDisplaySchema = z.enum(COURSE_PUBLIC_AUTHOR_DISPLAYS);
export type CoursePublicAuthorDisplay = (typeof COURSE_PUBLIC_AUTHOR_DISPLAYS)[number];

// ===== Проверка курса перед витриной =====

/**
 * Состояние проверки выпущенной версии курса (автомодерация публикации).
 *
 * - `pending`  — версия выпущена, проверка ещё не завершилась;
 * - `approved` — курс допущен на витрину;
 * - `rejected` — модель нашла нарушение правил каталога;
 * - `manual`   — решение за человеком: серая зона, отказ модели, killswitch.
 *
 * Публично видим только `approved`. Остальные три состояния означают «курс
 * скрыт», и различаются они лишь тем, что показать автору и что делать дальше:
 * `pending` ждёт, `manual` ждёт человека, `rejected` требует правок. Свести их в
 * булево «одобрен» нельзя — автор перестал бы отличать «ещё проверяем» от
 * «отклонено», а очередь модерации потеряла бы разницу между «модель не смогла»
 * и «модель нашла нарушение».
 */
export const COURSE_REVIEW_STATUSES = ["pending", "approved", "rejected", "manual"] as const;
export const courseReviewStatusSchema = z.enum(COURSE_REVIEW_STATUSES);
export type CourseReviewStatus = (typeof COURSE_REVIEW_STATUSES)[number];

// ===== Витринные тексты (listing) =====

/** Пара «вопрос — ответ» блока FAQ; она же строка в `FAQPage` JSON-LD. */
export const courseListingFaqItemSchema = z.object({
	question: z.string().min(1).max(300),
	answer: z.string().min(1).max(2000),
});
export type CourseListingFaqItem = z.infer<typeof courseListingFaqItemSchema>;

/**
 * Продающие тексты курса: то, что генерирует AI по снапшоту и правит автор
 * (миграция 0118, колонки `listing_*` у ОБОИХ источников каталога).
 *
 * Списки (`outcomes`, `faq`) — массивы, а не nullable-массивы: «пунктов нет» =
 * пустой массив, и секция просто не рендерится (§7.2 плана). Два способа
 * сказать «пусто» (`null` и `[]`) неизбежно разъедутся между сервером и UI, и
 * половина карточек начнёт рисовать пустую рамку.
 *
 * Одиночные тексты, наоборот, nullable: «описание ещё не сгенерировано» — не
 * то же самое, что «автор стёр описание в пустую строку».
 */
export const courseListingSchema = z.object({
	/** Краткое описание: карточка каталога и `<meta description>`. */
	summary: z.string().max(600).nullable(),
	/** Чему научится — буллеты блока «Результаты». */
	outcomes: z.array(z.string().min(1).max(300)).max(12),
	audience: z.string().max(600).nullable(),
	prerequisites: z.string().max(600).nullable(),
	faq: z.array(courseListingFaqItemSchema).max(20),
	/**
	 * Автор прочитал и подтвердил тексты в мастере публикации. Нужен не ради
	 * бейджа, а ради честности: сгенерированное описание может приукрасить
	 * содержание курса, и «проверено автором» — единственный способ отличить
	 * вычитанный текст от сырого (риск №3 плана).
	 */
	reviewedByAuthor: z.boolean(),
});
export type CourseListing = z.infer<typeof courseListingSchema>;

// ===== Совместимость с легаси-категориями =====

/**
 * Легаси-набор `course_templates.category` (до разделения осей). Держим здесь,
 * а не в UI: по нему бэкфиллит миграция и по нему же сервер добивает строки, у
 * которых `subject_area` ещё не заполнен — два разных маппинга разъехались бы.
 */
export const LEGACY_COURSE_TEMPLATE_CATEGORIES = [
	"exam",
	"language",
	"science",
	"humanities",
	"tech",
	"business",
	"medicine",
	"other",
] as const;

/**
 * Легаси-категория → предметная область.
 *
 * `exam` — не предмет, а формат: предметная область из него не выводится
 * (получаем `other`), зато он даёт `purpose = 'exam'` (см. `legacyPurpose`).
 * Ровно эта пара функций описывает бэкфилл миграции 0113.
 */
export function legacySubjectArea(category: string | null | undefined): CourseSubjectArea {
	switch (category) {
		case "language":
			return "languages";
		case "science":
		case "humanities":
		case "tech":
		case "business":
		case "medicine":
			return category;
		default:
			// `exam`, `other`, `null` и любая чужая строка: предмет неизвестен.
			return "other";
	}
}

/** Легаси-категория → назначение. Экзаменом курс делала именно категория `exam`. */
export function legacyPurpose(category: string | null | undefined): CoursePurpose {
	return category === "exam" ? "exam" : "general";
}

// ===== Запись каталога =====

/**
 * Карточка каталога — общая форма для обоих источников.
 *
 * `id` НЕ уникален между источниками сам по себе (две разные таблицы), поэтому
 * ключ карточки и все переходы строятся из пары `(origin, id)`.
 */
export const catalogEntrySchema = z.object({
	origin: catalogOriginSchema,
	id: z.string().min(1).max(64),
	/**
	 * Адрес публичной страницы (`/catalog/{slug}`).
	 *
	 * Nullable, в отличие от `coursePublicDetailSchema.slug`: каталог показывает
	 * и записи, до которых ещё не дошёл бэкфилл миграции 0118. Карточка без
	 * slug'а ведёт на приватный маршрут по `(origin, id)` — но НЕ выдумывает
	 * slug из названия: разошедшийся с сервером slug даёт 404 на клике.
	 */
	slug: z.string().max(200).nullable(),
	title: z.string().min(1).max(300),
	description: z.string().nullable(),
	subjectArea: courseSubjectAreaSchema,
	purpose: coursePurposeSchema,
	level: courseLevelSchema.nullable(),
	locale: localeSchema,
	/** Образовательная подсистема (`ru:ege`, `us:sat`…) — только у `purpose='exam'`. */
	educationSubsystem: z.string().max(64).nullable(),
	/** ISO-2 страны экзамена либо `OTHER`. */
	country: z.string().max(16).nullable(),
	/**
	 * Объём в минутах. У авторских — из текущей версии снапшота, у официальных —
	 * из оценки программы. `null` — объём неизвестен, и врать оценкой нельзя.
	 */
	estimatedMinutes: z.number().int().nonnegative().nullable(),
	/** Темы/разделы — только у авторских курсов: у программы их ещё нет. */
	topicCount: z.number().int().nonnegative().nullable(),
	sectionCount: z.number().int().nonnegative().nullable(),
	/** Сколько учеников уже начали (у официальных — счётчик не ведётся). */
	enrollmentsCount: z.number().int().nonnegative().nullable(),
	/** Обложка; все поля `null` — карточка рисует иконку-плитку. */
	cover: courseCoverSchema,
	/**
	 * Цена. Поле НЕ опциональное: «цена не пришла» и «курс бесплатный» карточке
	 * пришлось бы рисовать одинаково, и платный курс молча выглядел бы
	 * бесплатным. Программы платформы всегда отдают `FREE_PRICING`.
	 */
	pricing: coursePricingSchema,
	/** Автор — только у `origin='author'`. */
	authorName: z.string().max(200).nullable(),
	authorAvatarUrl: z.string().max(2000).nullable(),
	/**
	 * Курс этого пользователя, уже созданный по записи каталога (`enroll` для
	 * авторских). `null` — ещё не начинал. Считает СЕРВЕР: «уже начато» на
	 * клиенте выводить нечем.
	 */
	enrolledCourseId: z.string().min(1).max(64).nullable(),
	/**
	 * Состояние проверки — ТОЛЬКО для собственных курсов смотрящего.
	 *
	 * `null` у всех чужих карточек и у официальных программ. Это не косметика:
	 * посторонний не должен узнать из каталога, что чей-то курс отклонён
	 * модерацией. Своя карточка при этом обязана нести состояние — иначе автор
	 * видит курс в каталоге и считает, что он доступен всем.
	 */
	reviewStatus: courseReviewStatusSchema.nullable(),
});
export type CatalogEntry = z.infer<typeof catalogEntrySchema>;

// ===== Процедуры каталога =====

export const CATALOG_SORTS = ["popular", "new"] as const;
export const catalogSortSchema = z.enum(CATALOG_SORTS);
export type CatalogSort = (typeof CATALOG_SORTS)[number];

/**
 * Фильтры единого каталога. Все опциональны и складываются по «И»; ни один не
 * привязан к источнику — в этом и смысл универсальных фильтров.
 */
export const catalogListInput = cursorPaginationInput({
	defaultLimit: CATALOG_PAGE_SIZE,
	maxLimit: 48,
}).extend({
	subjectArea: courseSubjectAreaSchema.optional(),
	purpose: coursePurposeSchema.optional(),
	origin: catalogOriginSchema.optional(),
	level: courseLevelSchema.optional(),
	/** ФИЛЬТР по языку курса — не путать с `uiLocale` ниже. */
	locale: localeSchema.optional(),
	educationSubsystem: z.string().max(64).optional(),
	/**
	 * Фильтр «Бесплатные / Платные». Отсутствие значения = обе группы, а не
	 * «только бесплатные»: каталог по умолчанию показывает всё, что доступно.
	 */
	pricing: coursePricingKindSchema.optional(),
	query: z.string().max(200).optional(),
	sort: catalogSortSchema.default("popular"),
	/**
	 * Язык ИНТЕРФЕЙСА — на каком языке отдавать заголовки программ платформы
	 * (они лежат в jsonb `title_localized`). Это выбор отображения, и владеет им
	 * клиент; фолбэк внутри запроса — `en`, затем сырой `subject`.
	 */
	uiLocale: localeSchema.default("en"),
	/**
	 * Вход по НОМЕРУ страницы: сколько записей ленты пропустить.
	 *
	 * Второй способ войти в ту же самую последовательность — не вторая выдача.
	 * `ORDER BY` каталога полный (хвост ключа `(origin, id)` уникален), поэтому
	 * `OFFSET` и курсор идут по одним и тем же строкам в одном и том же порядке:
	 * робот приходит по ссылке `?page=3`, человек долистывает кнопкой — и видят
	 * они одно.
	 *
	 * `cursor` СИЛЬНЕЕ: пришли оба — работает курсор. Он точнее (не зависит от
	 * глубины) и приходит только от нашего же клиента, который его и получил.
	 *
	 * Потолок — `CATALOG_MAX_PAGE` страниц: без него публичный адрес принимал бы
	 * `?page=100000`, то есть `OFFSET` в миллионы на каждый заход переборщика.
	 */
	offset: z.number().int().min(0).max(catalogPageOffset(CATALOG_MAX_PAGE)).optional().default(0),
});
export const catalogListOutput = cursorPaginationOutput(catalogEntrySchema).extend({
	/**
	 * Сколько записей в ленте ПОД ТЕКУЩИМИ ФИЛЬТРАМИ — до курсора и смещения.
	 *
	 * Без него номерных страниц не нарисовать: клиент не знает, сколько их, а
	 * серверная голова не знает, где конец цепочки `rel="next"`. Считается тем же
	 * запросом (`COUNT(*) OVER ()`), поэтому не расходится с выдачей.
	 */
	total: z.number().int().nonnegative(),
});

/** Счётчик фасета: сколько записей каталога попадает в значение фильтра. */
export const catalogFacetCountSchema = z.object({
	value: z.string().min(1).max(64),
	count: z.number().int().nonnegative(),
});

/**
 * Счётчики фасетов для фильтр-бара. Считаются по ТЕМ ЖЕ правилам видимости,
 * что и выдача: «12» рядом с фильтром, который отдаёт 3 карточки, хуже, чем
 * отсутствие числа.
 */
export const catalogFacetsInput = z.object({
	locale: localeSchema.optional(),
	query: z.string().max(200).optional(),
	uiLocale: localeSchema.default("en"),
});
export const catalogFacetsOutput = z.object({
	subjectAreas: z.array(catalogFacetCountSchema),
	purposes: z.array(catalogFacetCountSchema),
	origins: z.array(catalogFacetCountSchema),
	/**
	 * Счётчики `free` / `paid`. Отдаются всегда, даже когда платных курсов нет
	 * ни одного (`count: 0`): решение «показывать ли ось фильтра» принимает
	 * фильтр-бар по счётчикам, и молчание сервера он не отличит от нуля.
	 */
	pricing: z.array(catalogFacetCountSchema),
	total: z.number().int().nonnegative(),
});

/**
 * Подборка каталога для главной страницы сервиса.
 *
 * Не разновидность `catalogList` с другими фильтрами: там лента с курсором,
 * фасетами и поиском, здесь — короткая витрина фиксированного размера, где
 * порядок задаёт не сортировка, а выборка (`_internal/pick-catalog-highlights`).
 * Общего у них только DTO карточки — и это правильно: карточка на главной и в
 * каталоге обязаны выглядеть одинаково.
 */
export const catalogHighlightsInput = z.object({
	/** Сколько карточек показать в слайдере. */
	limit: z.number().int().min(1).max(24).default(12),
	/** Язык интерфейса — тот же смысл, что у `catalogList`. */
	uiLocale: z.string().min(2).max(12).optional(),
});

export const catalogHighlightsOutput = z.object({
	items: z.array(catalogEntrySchema),
});
